const TARGET_STORAGE_KEY = "deepdd_api_targets";
const DEFAULT_TARGETS = {
  environment: "test",
  test_url: process.env.REACT_APP_DEEP_DD_TEST_API_BASE || `${window.location.origin}/3D/business`,
  // test_url: process.env.REACT_APP_DEEP_DD_TEST_API_BASE || "https://dev2.4pay.ca/portal/www/3D/business",
  production_url: process.env.REACT_APP_DEEP_DD_PRODUCTION_API_BASE || `${window.location.origin}/3D/business`,
  // production_url: process.env.REACT_APP_DEEP_DD_PRODUCTION_API_BASE || "https://dev2.4pay.ca/3D/business",
};

console.log("DEFAULT_TARGETS--------", DEFAULT_TARGETS);
console.log("test_url--------", DEFAULT_TARGETS?.test_url);

export const getDeepDDTargets = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(TARGET_STORAGE_KEY) || "{}");
    return { ...DEFAULT_TARGETS, ...saved };
  } catch (error) {
    return DEFAULT_TARGETS;
  }
};

export const saveDeepDDTargets = (targets) => {
  const normalized = {
    environment: targets.environment === "production" ? "production" : "test",
    test_url: String(targets.test_url || DEFAULT_TARGETS.test_url).replace(/\/$/, ""),
    production_url: String(targets.production_url || DEFAULT_TARGETS.production_url).replace(/\/$/, ""),
  };
  window.localStorage.setItem(TARGET_STORAGE_KEY, JSON.stringify(normalized));
  window.dispatchEvent(new CustomEvent("deepdd-api-target-changed", { detail: normalized }));
  return normalized;
};

export const getDeepDDBase = () => {
  const targets = getDeepDDTargets();
  const base = targets.environment === "production"
    ? targets.production_url
    : targets.test_url;
  return String(base || DEFAULT_TARGETS.test_url).replace(/\/$/, "");
};

const gatewayErrorMessage = (status) => {
  if (status === 504) {
    return "DeepDD gateway timed out (HTTP 504). The workflow may still be finishing on the server; check the run history before retrying.";
  }

  if (status === 502 || status === 503) {
    return `DeepDD is temporarily unavailable (HTTP ${status}). Please try again shortly.`;
  }

  return `DeepDD request failed (HTTP ${status}).`;
};

const deepDDErrorMessage = (data, fallback) => {
  const lines = [data?.error || fallback];
  if (data?.technical_error) lines.push(data.technical_error);
  if (data?.technical_location) lines.push(`Location: ${data.technical_location}`);
  if (data?.error_id) lines.push(`Error ID: ${data.error_id}`);
  return lines.filter(Boolean).join("\n");
};

export const parseDeepDDResponse = async (response) => {
  const text = await response.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : {};
  } catch (error) {
    if (response.ok) {
      throw new Error("DeepDD returned an unreadable response. Please check the server logs and try again.");
    }
  }

  if (!response.ok) {
    throw new Error(deepDDErrorMessage(data, gatewayErrorMessage(response.status)));
  }

  if (data?.ok === false) {
    throw new Error(deepDDErrorMessage(data, "DeepDD could not complete the request."));
  }

  return data;
};

export const deepDDFetch = async (path, options = {}) => {
  const response = await fetch(`${getDeepDDBase()}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      // The PHP services read the raw request body, so text/plain keeps these
      // cross-origin JSON calls simple while preserving the same payload.
      ...(options.body ? { "Content-Type": "text/plain;charset=UTF-8" } : {}),
      ...(options.headers || {}),
    },
  });

  return parseDeepDDResponse(response);
};

export const postDeepDD = (path, payload) =>
  deepDDFetch(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getHistory = () => deepDDFetch("/ui/history_api.php");

export const getAuditReview = (investigationId) =>
  deepDDFetch(`/ui/history_api.php?audit=${encodeURIComponent(investigationId)}`);

export const getAuditReviews = () => deepDDFetch("/ui/history_api.php?audits=1");

export const getReportActivity = ({ range = "this_month", startDate = "", endDate = "" } = {}) => {
  const params = new URLSearchParams({ range });
  if (range === "custom") {
    params.set("start_date", startDate);
    params.set("end_date", endDate);
  }
  return deepDDFetch(`/ui/report_activity_api.php?${params.toString()}`);
};

export const initializeInvestigation = (subject) =>
  postDeepDD("/run/run_step1.php", subject);

const runCrawlStep = async (step, investigationId) => {
  const query = `?investigation_id=${encodeURIComponent(investigationId)}`;
  let offset = 0;
  let result;

  do {
    result = await deepDDFetch(`${step.path}${query}`, {
      method: "POST",
      body: JSON.stringify({ investigation_id: investigationId, offset, limit: 5 }),
    });

    const nextOffset = Number(result.next_offset);
    if (!result.completed && (!Number.isFinite(nextOffset) || nextOffset <= offset)) {
      throw new Error("Crawl did not return valid progress.");
    }
    offset = nextOffset;
  } while (!result.completed);

  return result;
};

export const runWorkflowStep = async (step, investigationId, subject) => {
  try {
    if (step.key === "initialize") {
      return await initializeInvestigation(subject);
    }

    if (step.key === "crawl") {
      return await runCrawlStep(step, investigationId);
    }

    const query = `?investigation_id=${encodeURIComponent(investigationId)}`;
    const options = { method: step.method };

    if (step.method === "POST") {
      options.body = JSON.stringify({ investigation_id: investigationId });
    }

    return await deepDDFetch(`${step.path}${query}`, options);
  } catch (error) {
    throw new Error(`${step.label} failed: ${error.message}`);
  }
};

export const hubSpotRequest = (payload) =>
  postDeepDD("/ui/hubspot_api.php", payload);

export const WORKFLOW_STEPS = [
  { key: "initialize", number: "1", label: "Subject", method: "POST", path: "/run/run_step1.php" },
  { key: "discovery", number: "2", label: "Discovery", method: "POST", path: "/run/run_step2_discovery.php" },
  { key: "crawl", number: "3", label: "Crawl", method: "POST", path: "/run/run_step3_crawl.php" },
  { key: "extract", number: "4", label: "Extract", method: "POST", path: "/run/run_step4_extract.php" },
  { key: "leadership", number: "5", label: "Leadership", method: "GET", path: "/run/run_step5_leadership.php" },
  { key: "social", number: "6A", label: "Social", method: "GET", path: "/run/run_step6_social.php" },
  { key: "military", number: "6B", label: "Military", method: "POST", path: "/run/run_step6b_military.php" },
  { key: "darkweb", number: "6C", label: "Dark Web", method: "POST", path: "/run/run_step6c_darkweb.php" },
  { key: "domain", number: "7", label: "Domain", method: "GET", path: "/run/run_step7_domain.php" },
  { key: "ofac", number: "8", label: "OFAC", method: "GET", path: "/run/run_step8_ofac.php" },
  { key: "ai", number: "9", label: "AI Review", method: "GET", path: "/run/run_step9_ai.php" },
  { key: "risk", number: "10", label: "Risk", method: "GET", path: "/run/run_step10_risk.php" },
  { key: "report", number: "11", label: "Report", method: "GET", path: "/run/run_step11_report.php" },
];
